import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Save, Wallet, Building2, User, Landmark, BookUp2, X, FileText, Plus, Receipt, ScrollText } from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { AccountMovement } from "../../../types/accountMovement";
import { useCustomerStore } from "../../../store/customerStore";
import { useCashStore } from "../../../store/cashStore";
import { useBankStore } from "../../../store/bankStore";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { CustomDateInput } from "../../../components/CustomDateInput";
import { Customer } from "../../../types/customer";
import { Cash } from "../../../types/cash";
import { Bank } from "../../../types/bank";
import { Filter } from "../../../types";
import { toast } from "react-toastify";
import { useCurrencyStore } from "../../../store/currencyStore";

interface CollectionPaymentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  movement: AccountMovement | null;
  onSave: (data: any) => void;
}

export const CollectionPaymentDialog: React.FC<CollectionPaymentDialogProps> = ({
  isOpen, onClose, movement, onSave,
}) => {
  const { fetchDataDynamic: fetchCustomers, loading: customerLoading } = useCustomerStore();
  const { fetchDataDynamic: fetchCashes, loading: cashLoading } = useCashStore();
  const { fetchDataDynamic: fetchBanks, loading: bankLoading } = useBankStore();
  const { getLiveRates } = useCurrencyStore();

  const customerSelect = useSelectDialog<Customer>();
  const cashSelect = useSelectDialog<Cash>();
  const bankSelect = useSelectDialog<Bank>();

  const [formData, setFormData] = useState<any>({
    type: 'COLLECTION_IN',
    paymentLocation: 'CASH',
    accountId: null,
    accountName: "",
    cashId: null,
    cashName: "",
    bankId: null,
    bankName: "",
    amount: 0,
    currency: "TRY",
    exchangeRate: 1,
    reference: "",
    description: "",
    date: new Date(),
    dueDate: null,
  });

  const initialFormData = {
    type: 'COLLECTION_IN',
    paymentLocation: 'CASH',
    accountId: null,
    accountName: "",
    cashId: null,
    cashName: "",
    bankId: null,
    bankName: "",
    amount: 0,
    currency: "TRY",
    exchangeRate: 1,
    reference: "",
    description: "",
    date: new Date(),
    dueDate: null,
  };

  useEffect(() => {
    const updateRate = async () => {
      if (!movement?.id && formData.currency !== 'TRY') {
        const rates = await getLiveRates();
        const currencyKey = formData.currency as keyof typeof rates;
        const newRate = rates[currencyKey] || 1;

        setFormData((prev: typeof initialFormData) => ({
          ...prev,
          exchangeRate: newRate
        }));
      }
    };

    updateRate();
  }, [formData.currency, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (movement?.id) {
      let mappedLocation = 'NONE';
      if (movement.paymentLocation === 'CASH') mappedLocation = 'CASH';
      if (movement.paymentLocation === 'BANK') mappedLocation = 'BANK';
      if (movement.paymentLocation === 'OTHER') mappedLocation = 'NONE';

      setFormData({
        ...movement,
        type: movement.type || 'COLLECTION_IN',
        accountName: movement.account?.name || "",
        cashName: movement.cash?.name || "",
        bankName: movement.bank?.name || "",
        date: new Date(movement.date || movement.createdAt || new Date()),
        dueDate: movement.dueDate ? new Date(movement.dueDate) : null,
        paymentLocation: mappedLocation
      });
    } else if (movement?.type) {
      setFormData({
        ...initialFormData,
        type: movement.type || 'COLLECTION_IN',
        paymentLocation: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(movement.type) ? 'NONE' : 'CASH',
        date: new Date(),
        dueDate: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(movement.type) ? new Date() : null,
        amount: 0
      });
    }
  }, [isOpen, movement]);

  const openCustomerSelect = async () => {
    const res = await fetchCustomers({ pageIndex: 1, pageSize: 10 });
    const selected = await customerSelect.open({
      title: "Müşteri / Cari Seç",
      data: res.items || [],
      loading: customerLoading,
      columns: [{ field: "id", header: "ID" }, { field: "name", header: "Müşteri Adı" }],
      onFilter: async (p) => {
        const r = await fetchCustomers({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) setFormData({ ...formData, accountId: selected.id, accountName: selected.name });
  };

  const openCashSelect = async () => {
    const res = await fetchCashes({ pageIndex: 1, pageSize: 10 });
    const selected = await cashSelect.open({
      title: "Kasa Seç",
      data: res.items || [],
      loading: cashLoading,
      columns: [{ field: "name", header: "Kasa Adı" }, { field: "balance", header: "Bakiye" }],
      onFilter: async (p) => {
        const r = await fetchCashes({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) setFormData({ ...formData, cashId: selected.id, cashName: selected.name });
  };

  const openBankSelect = async () => {
    const res = await fetchBanks({ pageIndex: 1, pageSize: 10 });
    const selected = await bankSelect.open({
      title: "Banka Hesabı Seç",
      data: res.items || [],
      loading: bankLoading,
      columns: [{ field: "name", header: "Banka/Şube" }, { field: "iban", header: "IBAN" }],
      onFilter: async (p) => {
        const r = await fetchBanks({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter as Filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) setFormData({ ...formData, bankId: selected.id, bankName: selected.name });
  };

  const isCheckOrPromissory = ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(formData.type);

  const handleSubmit = () => {
    const isLocationValid =
      isCheckOrPromissory ||
      formData.paymentLocation === 'NONE' ||
      (formData.paymentLocation === 'CASH' && formData.cashId) ||
      (formData.paymentLocation === 'BANK' && formData.bankId);

    if (!formData.accountId || formData.amount <= 0 || !isLocationValid) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }

    const isInDirection = ['COLLECTION_IN', 'CHECK_IN', 'PROMISSORY_IN'].includes(formData.type);

    const payload: any = {
      type: formData.type,
      direction: isInDirection ? 'IN' : 'OUT',
      accountId: Number(formData.accountId),
      amount: Number(formData.amount),
      currency: formData.currency,
      exchangeRate: formData.exchangeRate,
      reference: formData.reference || undefined,
      description: formData.description || undefined,
      paymentLocation: isCheckOrPromissory ? 'OTHER' : (formData.paymentLocation === 'NONE' ? 'OTHER' : formData.paymentLocation),
      date: formData.date,
      dueDate: formData.dueDate || undefined,
    };

    if (!isCheckOrPromissory) {
      if (formData.paymentLocation === 'CASH' && formData.cashId) {
        payload.cashId = Number(formData.cashId);
      } else if (formData.paymentLocation === 'BANK' && formData.bankId) {
        payload.bankId = Number(formData.bankId);
      }
    }

    onSave(payload);
  };

  const typeOptions = [
    { label: 'Tahsilat (Nakit/Banka)', value: 'COLLECTION_IN' },
    { label: 'Ödeme (Nakit/Banka)', value: 'PAYMENT_OUT' },
    { label: 'Müşteri Çeki Girişi', value: 'CHECK_IN' },
    { label: 'Çek Çıkışı / Ciro', value: 'CHECK_OUT' },
    { label: 'Müşteri Senet Girişi', value: 'PROMISSORY_IN' },
    { label: 'Senet Çıkışı', value: 'PROMISSORY_OUT' },
  ];

  const locationOptions = [
    { label: 'Kasa', value: 'CASH', icon: Wallet },
    { label: 'Banka', value: 'BANK', icon: Landmark },
    { label: 'Diğer / Çek / Senet', value: 'NONE', icon: FileText }
  ];

  const getDialogTitle = () => {
    switch (formData.type) {
      case 'CHECK_IN': return 'Müşteri Çeki Girişi';
      case 'CHECK_OUT': return 'Çek Çıkışı / Ciro';
      case 'PROMISSORY_IN': return 'Müşteri Senet Girişi';
      case 'PROMISSORY_OUT': return 'Senet Çıkışı';
      case 'PAYMENT_OUT': return 'Ödeme Yap (Tediye)';
      default: return 'Tahsilat Ekle';
    }
  };

  return (
    <>
      <Dialog
        header={getDialogTitle()}
        visible={isOpen} onHide={onClose} className="w-full lg:w-5/12"
        maximizable
        resizable={false}
        footer={
          <div className="flex justify-end gap-3">
            <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
            <IconTextButton onClick={handleSubmit} icon={formData.id ? Save : Plus} className="btn-new">Kaydet</IconTextButton>
          </div>
        }
      >
        <div className="space-y-4 pt-2">
          {/* İşlem Türü Seçimi */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-bold uppercase opacity-70">İşlem Türü *</label>
            <select
              value={formData.type}
              onChange={(e) => {
                const newT = e.target.value;
                setFormData({
                  ...formData,
                  type: newT,
                  paymentLocation: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(newT) ? 'NONE' : 'CASH',
                  dueDate: ['CHECK_IN', 'CHECK_OUT', 'PROMISSORY_IN', 'PROMISSORY_OUT'].includes(newT) ? (formData.dueDate || new Date()) : null
                });
              }}
              className="p-2.5 rounded-lg border border-borderColor bg-card text-text text-sm font-semibold focus:outline-none focus:border-primary"
            >
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Müşteri Seçimi (Hook İle) */}
          <div className="flex flex-col gap-2">
            <CustomSelectInput
              label="Müşteri / Cari *"
              value={formData.accountName || ""}
              placeholder="Seçmek için tıklayın..."
              icon={User}
              onClick={openCustomerSelect}
            />
          </div>

          {/* Kanal Seçimi (Kasa/Banka sadece nakit işlemlerde) */}
          {!isCheckOrPromissory && (
            <>
              <div className="grid grid-cols-3 gap-3">
                {locationOptions.map((opt) => (
                  <div key={opt.value} onClick={() => setFormData({ ...formData, paymentLocation: opt.value })}
                    className={`card cursor-pointer p-3 transition-all flex flex-col items-center gap-2 ${formData.paymentLocation === opt.value ? 'border-primary shadow-md' : 'card2 opacity-70 hover:opacity-100'}`}>
                    <opt.icon size={20} className={formData.paymentLocation === opt.value ? 'text-primary' : ''} />
                    <span className="text-xs font-semibold">{opt.label}</span>
                  </div>
                ))}
              </div>

              {formData.paymentLocation === 'CASH' && (
                <div className="flex flex-col gap-2 animate-in slide-in-from-top-2 duration-200">
                  <CustomSelectInput
                    label="Kasa Hesabı *"
                    value={formData.cashName || ""}
                    placeholder="Kasa Seçiniz..."
                    icon={Wallet}
                    onClick={openCashSelect}
                  />
                </div>
              )}

              {formData.paymentLocation === 'BANK' && (
                <div className="flex flex-col gap-2 animate-in slide-in-from-top-2 duration-200">
                  <CustomSelectInput
                    label="Banka Hesabı *"
                    value={formData.bankName || ""}
                    placeholder="Banka Seçiniz..."
                    icon={Landmark}
                    onClick={openBankSelect}
                  />
                </div>
              )}
            </>
          )}

          {/* Finansal Bilgiler Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold">Döviz</label>
              <div className="flex gap-1">
                {['TRY', 'USD', 'EUR'].map((cur) => (
                  <button key={cur} onClick={() => setFormData({ ...formData, currency: cur, exchangeRate: cur === 'TRY' ? 1 : formData.exchangeRate })}
                    className={`flex-1 p-2 text-xs font-bold rounded border ${formData.currency === cur ? 'bg-primary text-white border-primary' : 'bg-card border-gray-600'}`}>
                    {cur}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-bold">Kur (1 {formData.currency})</label>
                {formData.currency !== 'TRY' && (
                  <span className="text-[10px] bg-green-500/20 text-green-500 px-1 rounded animate-pulse">
                    SİSTEMDEN ALINDI
                  </span>
                )}
              </div>
              <InputNumber
                value={formData.exchangeRate}
                onValueChange={(e) => setFormData({ ...formData, exchangeRate: e.value })}
                disabled={formData.currency === 'TRY'}
                minFractionDigits={2}
                maxFractionDigits={4}
                placeholder="Kur giriniz..."
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold">Tutar *</label>
              <InputNumber value={formData.amount} onValueChange={(e) => setFormData({ ...formData, amount: e.value })}
                mode="currency" currency={formData.currency === 'TL' ? 'TRY' : (formData.currency || 'TRY')} locale="tr-TR" />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-bold">{isCheckOrPromissory ? 'Çek / Belge No' : 'Belge No'}</label>
              <InputText
                value={formData.reference || ""}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="Örn: Çek No veya Dekont No"
              />
            </div>

            <div className="flex flex-col gap-2">
              <CustomDateInput
                label="İşlem Tarihi *"
                value={formData.date}
                onChange={(val) => setFormData({ ...formData, date: val as Date })}
                selectionMode="single"
              />
            </div>

            {/* Vade Tarihi (Özellikle Çek & Senet için) */}
            <div className="flex flex-col gap-2">
              <CustomDateInput
                label={isCheckOrPromissory ? "Vade Tarihi *" : "Vade Tarihi (Opsiyonel)"}
                value={formData.dueDate}
                onChange={(val) => setFormData({ ...formData, dueDate: val as Date })}
                selectionMode="single"
              />
            </div>

            <div className="flex flex-col gap-2 col-span-2">
              <label className="text-sm font-bold">Açıklama</label>
              <InputTextarea value={formData.description || ""} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
          </div>
        </div>
      </Dialog>

      {customerSelect.modal}
      {cashSelect.modal}
      {bankSelect.modal}
    </>
  );
};
